/**
 * Output Format Schema - XML-wrapped JSON schema
 * Aligned with PROMPT_BUILDER_SCHEMA (flat, snake_case): toggle-gated fields,
 * plus `improvements` (always emitted). The «said / not said» checks
 * (mandatory phrases, profanity, recording disclaimer, compliance verdicts) are
 * not part of the main analysis since Э3 (#2846): built-in criteria own them.
 *
 * IMPORTANT: The example JSON structure here MUST match PROMPT_BUILDER_SCHEMA
 * in packages/core/src/schemas/prompt-builder.ts exactly.
 * PROMPT_BUILDER_SCHEMA is the OpenAI Structured Output contract that enforces the LLM output.
 * The example shows the fields the strict schema accepts for the toggles it is
 * given — a disabled toggle is absent from both, never present as null.
 *
 * Mind WHICH toggles the caller passes. `buildXMLSystemPrompt` deliberately
 * passes the widest set the channel allows (ALL_TOGGLES_ON masked by
 * `resolveChannelToggles`) so the system prompt stays cache-stable across
 * projects, while the analyze worker builds the strict schema from the
 * record's own toggles. The example is therefore a superset of what a given
 * record accepts, and the instructions below say so: the authoritative
 * per-record list is `<analysis_toggles>` in the user prompt.
 *
 * Category-aware: uses callCategory for isSales determination when available.
 */

import type { PromptToggles } from '@aica/core';
import type { CallCategory } from '../call-categories';
import { isSalesCategory } from '../call-categories';

type BaseCategory = 'sales-call' | 'support-call' | 'sales-chat' | 'complaint';

interface OutputFormatOptions {
  toggles: PromptToggles;
  category: BaseCategory;
  callCategory?: CallCategory;
}

/**
 * Generate XML-wrapped output format aligned with PROMPT_BUILDER_SCHEMA
 */
export function getOutputFormatSchema(options: OutputFormatOptions): string {
  const { toggles, category, callCategory } = options;

  const sentimentInstructions = toggles.sentiment
    ? `
    - SENTIMENT: Use NUMERIC values (-1.0 to +1.0), NOT text like "positive"/"neutral"
    - Score CLIENT sentiment only (do NOT score the agent/employee)
    - MUST include: client_start, client_end, delta, overall`
    : '';

  return `
<output_format>
  <schema type="json">
${buildFlatJSONExample(toggles, category, callCategory)}
  </schema>

  <instructions>
    - Return ONLY valid JSON (no markdown, no text outside JSON)
    - CRITICAL: Use FLAT structure with every field at root level (snake_case)
    - Every field defined by the strict JSON schema MUST be present, and no others. The example above shows the widest set this conversation's channel and configuration can accept; the &lt;analysis_toggles&gt; block in the request is authoritative for THIS conversation, and it names TOGGLES, not JSON keys. Return every field the example shows, except that you OMIT the rest — every field whose toggle that block lists as disabled — completely — a disabled field is absent from the schema, so returning it is rejected, and returning it as null is rejected too. improvements is always emitted (never null).${sentimentInstructions}
    - All scores: 0-100 range, EXCEPT where a field's own guideline says to return null
      for "not applicable" (e.g. closing_techniques.score, crm_completeness.score,
      needs_qualification.score — see their <guideline> entries above). All
      probabilities: 0.0-1.0 range
    - All evidence must reference specific transcript moments
  </instructions>

  <validation_requirements>
    <consistency_check>
      - All referenced timestamps must exist in transcript
      - quality_score must be 0-100
      - sale_probability must be 0.0-1.0
      - sentiment values must be -1.0 to +1.0
    </consistency_check>
  </validation_requirements>
</output_format>`;
}

/**
 * Build flat JSON example matching PROMPT_BUILDER_SCHEMA exactly.
 * Each field is either an example value (toggle ON) or null (toggle OFF).
 */
function buildFlatJSONExample(
  toggles: PromptToggles,
  category: BaseCategory,
  callCategory?: CallCategory
): string {
  const isSales = callCategory
    ? isSalesCategory(callCategory)
    : category === 'sales-call' || category === 'sales-chat';
  const schema: Record<string, unknown> = {};

  // === A. Summary Metrics ===
  schema.quality_score = toggles.qualityScore ? 78 : null;

  schema.sale_probability = toggles.saleProbability && isSales ? 0.55 : null;

  // Nothing in this example is an attention trigger — comparing options is not
  // defection and its operator errors are below MEDIUM — so the flag stays
  // not triggered.
  schema.attention_flag = toggles.attentionFlag
    ? {
        triggered: false,
        severity: 'low',
        reason: 'No attention trigger applies: the customer is still comparing options',
      }
    : null;

  // === B. Content Analysis ===
  schema.topic = toggles.topic ? 'CRM software evaluation' : null;

  schema.keywords = toggles.keywords
    ? ['CRM', 'budget', 'integration', 'timeline']
    : null;

  // The mark exists in the schema only under attentionFlag; the example's flag
  // is not triggered, so no moment carries it.
  const flagMark = toggles.attentionFlag ? { supports_attention_flag: false } : {};
  schema.key_moments = toggles.keyMoments
    ? [
        {
          timestamp: '02:15',
          label: 'Budget revealed',
          quote: '$50K range mentioned',
          importance: 'high',
          speaker: 'customer',
          ...flagMark,
        },
        {
          timestamp: '05:23',
          label: 'Price objection',
          quote: "That's expensive for us",
          importance: 'medium',
          speaker: 'customer',
          ...flagMark,
        },
      ]
    : null;

  schema.summary = toggles.summary
    ? 'Sales call discussing CRM software. Agent qualified budget ($50K) and need (10h/week lost), but got only a vague timeline.'
    : null;

  schema.structured_summary = toggles.structuredSummary
    ? {
        situation: 'Prospect evaluating CRM solutions, currently losing 10h/week',
        actions: [
          'Presented product features',
          'Qualified budget',
          'Handled price objection',
        ],
        outcome: 'Interest shown but no timeline commitment',
      }
    : null;

  // === C. Speech Analysis ===
  // Пустое значение и в примере: счёт ведёт код (#819), а пример с числом
  // модель копировала бы вопреки инструкции.
  schema.filler_words = toggles.fillerWords ? { count: 0, examples: [] } : null;

  schema.stop_words = toggles.stopWords
    ? {
        detected: [
          {
            token: 'мабуть',
            speaker: 'agent',
            quote: 'Ну, мабуть, ми це зможемо зробити.',
          },
        ],
      }
    : null;

  schema.operator_errors = toggles.operatorErrors
    ? [
        {
          type: 'unexplained_jargon',
          description: 'Used internal jargon at 04:30',
          kind: 'said',
          evidence_quote: 'Це піде через наш SLA-тріаж',
        },
        {
          type: 'no_next_step',
          description: 'Did not confirm the follow-up date',
          kind: 'omitted',
          evidence_quote: 'Добре, тоді якось зідзвонимось',
        },
      ]
    : null;

  // === D. Dialogue Technique ===
  schema.greeting_intro = toggles.greetingIntro
    ? {
        score: 75,
        feedback: 'Named the customer, company and purpose; no rapport built',
      }
    : null;

  schema.needs_qualification = toggles.needsQualification
    ? { score: 75, identified_needs: ['budget ($50K)', 'need (10h/week lost)'] }
    : null;

  schema.objection_handling = toggles.objectionHandling
    ? {
        objections: [
          // `type` is the free label, `code` the canonical reason; NEITHER item
          // repeats its code as the label, so the model does not learn to copy
          // one into the other. The first item is the price objection that
          // key_moments and structured_summary of this same example describe
          // (same quote) — an example whose key moment has no counterpart in
          // objections[] would teach the model to skip objections.
          {
            type: 'too expensive',
            score: 83,
            quote: "That's expensive for us",
            code: 'price',
            secondary_code: null,
          },
          {
            type: 'wait until next quarter',
            score: 50,
            quote: 'We need to wait until next quarter, and the board must sign off',
            code: 'timing',
            secondary_code: 'decision_authority',
          },
        ],
      }
    : null;

  schema.closing_techniques = toggles.closingTechniques
    ? { score: 60, techniques_used: ['trial_close: "How does that sound?" at 08:45'] }
    : null;

  // === E. Emotions ===
  schema.sentiment = toggles.sentiment
    ? {
        client_start: -0.2,
        client_end: 0.5,
        delta: 0.7,
        overall: 0.4,
      }
    : null;

  schema.politeness = toggles.politeness
    ? { score: 85, polite_phrases: ['please', 'thank you', 'I appreciate'] }
    : null;

  schema.interruptions = toggles.interruptions
    ? { count: 3, by_agent: 2, by_customer: 1 }
    : null;

  schema.clarity = toggles.clarity
    ? { score: 85, feedback: 'Clear articulation, minimal jargon' }
    : null;

  // === F. Compliance ===
  schema.crm_completeness = toggles.crmCompleteness
    ? { applicable: true, score: 67, missing_fields: ['company'] }
    : null;

  // === H. Business Intelligence ===
  schema.business_intelligence = toggles.businessIntelligence
    ? {
        competitor_mentions: [
          {
            name: 'John Deere',
            context: 'Customer compared JD 1010 on ease of service',
            sentiment: 'positive',
          },
        ],
        product_feedback: [
          {
            product: 'CRM-300',
            feedback: 'Firmware crashes after latest update, had to be reflashed',
            severity: 'incident',
          },
        ],
        market_signals: [
          {
            signal: 'Cooperative (колгосп) planning bulk purchase next quarter',
            type: 'demand',
          },
        ],
        compliance_flags: [
          {
            flag: 'alternate_entity_purchase',
            context:
              "Customer proposed invoicing through a relative's ФОП to avoid paperwork",
          },
        ],
      }
    : null;

  // === G. Recommendations ===
  schema.strengths_improvements = toggles.strengthsImprovements
    ? {
        strengths: [
          {
            behavior: 'Excellent empathy and rapport building',
            impact: 'Customer opened up about real needs',
          },
        ],
        improvements: [
          {
            behavior: 'No timeline qualification',
            suggestion: 'Ask "When do you need this in place?" within first 3 minutes',
          },
        ],
      }
    : null;

  // Always emitted (not toggle-gated): per-criterion coaching plan. Each
  // criterion_key MUST be one of the criterion keys scored above (e.g.
  // objection_handling, needs_qualification) so coaching aggregates by real
  // criteria. Empty array when the call needs no coaching.
  schema.improvements = [
    {
      criterion_key: 'objection_handling',
      advice: 'Acknowledge the timing objection before reframing to value',
      priority: 'high',
      example_quote: 'We need to wait until next quarter',
    },
  ];

  schema.extracted_crm_fields = toggles.extractedCrmFields
    ? [
        {
          field_id: 'UF_CRM_BUDGET',
          value: '50000',
          confidence: 0.82,
          evidence_quote: 'бюджет около 50 тысяч',
        },
      ]
    : null;

  schema.agreements = toggles.agreements
    ? [
        {
          type: 'call',
          subject: 'Перезвонить по КП',
          description: 'Клиент попросил перезвонить завтра в первой половине дня',
          due_hint: 'завтра утром',
          due_at: '',
          responsible_hint: '',
          confidence: 0.9,
          evidence_quote: 'перезвоните мне завтра утром',
          evidence_timestamp: '04:32',
        },
      ]
    : null;

  // Show only what the strict schema will actually accept.
  //
  // `buildAnalysisSchema` (@aica/core) does not merely mark a disabled field
  // nullable — it REMOVES it from `properties` and from `required`, and the
  // schema is sent with `strict: true` and `additionalProperties: false`. A
  // key printed here as `null` is therefore a key the model is forbidden to
  // return: the example asked for output the schema rejects, and the
  // "DISABLED FIELDS (return null for these)" line asked for it in words too.
  // Structured output makes that request unfulfillable rather than harmful,
  // so nothing was visibly broken — it just spent context contradicting the
  // contract. Pinned by output-format-matches-schema.test.ts, which compares
  // this key set against the schema's own properties.
  const emitted = Object.fromEntries(
    Object.entries(schema).filter(([, value]) => value !== null)
  );

  return JSON.stringify(emitted, null, 2);
}
